import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import DataTable from 'react-data-table-component';
import { tbqFunctions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';
import UserEditForm from './UserEditForm';

const FilterComponent = ({
  filterText,
  onFilter,
  isAdmin,
  locations,
  selectedLocation,
  onLocationChange,
  onLocationClear,
  selectedAttendingFilter,
  onAttendingFilterChange,
  selectedGenderFilter,
  onGenderFilterChange,
}) => (
  <div className="filters-row">
    <input
      type="text"
      placeholder="Search by Name, Email, or Mobile"
      aria-label="Search Input"
      value={filterText}
      onChange={onFilter}
      className="filter-input"
    />
    {isAdmin ? (
      <>
        <div className="location-filter-wrap">
          <input
            type="text"
            list="users-location-options"
            className="filter-select"
            value={selectedLocation}
            onChange={onLocationChange}
            aria-label="Filter by location"
            placeholder="All Locations"
          />
          {selectedLocation ? (
            <button
              type="button"
              className="btn-clear-location"
              onClick={onLocationClear}
              aria-label="Clear location filter"
              title="Clear"
            >
              x
            </button>
          ) : null}
          <datalist id="users-location-options">
            {locations.map((location) => (
              <option key={location} value={location} />
            ))}
          </datalist>
        </div>
        <select
          className="filter-select"
          value={selectedAttendingFilter}
          onChange={onAttendingFilterChange}
          aria-label="Filter by attending mode"
        >
          <option value="">All Users</option>
          <option value="attending">Attending at Location</option>
          <option value="online">Online</option>
        </select>
        <select
          className="filter-select"
          value={selectedGenderFilter}
          onChange={onGenderFilterChange}
          aria-label="Filter by gender"
        >
          <option value="">All Genders</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
        </select>
      </>
    ) : null}
  </div>
);

const convertToCSV = (objArray, columns) => {
  const headers = columns
    .filter((col) => col.name !== 'Coordinator' && col.name !== 'Actions')
    .map(column => column.name).join(',') + '\r\n';
  const rows = objArray.map(obj => {
    return columns
      .filter((col) => col.name !== 'Coordinator' && col.name !== 'Actions')
      .map(column => {
        const selector = typeof column.selector === 'function' ? column.selector(obj) : obj[column.selector];
        const value = selector ? selector.toString() : '';
        return `"${value.replace(/"/g, '""')}"`;
      }).join(',');
  }).join('\r\n');

  return headers + rows;
};

const downloadCSV = (csv, filename) => {
  const csvFile = new Blob([csv], { type: "text/csv" });
  const downloadLink = document.createElement("a");
  downloadLink.download = filename;
  downloadLink.href = window.URL.createObjectURL(csvFile);
  downloadLink.style.display = "none";
  document.body.appendChild(downloadLink);
  downloadLink.click();
};

const customStyles = {
  headCells: {
    style: {
      backgroundColor: '#183D24',
      color: '#ffffff',
      fontWeight: '600',
      fontSize: '13px',
    },
  },
  cells: {
    style: {
      fontSize: '13px',
      padding: '8px 12px',
    },
  },
  rows: {
    highlightOnHoverStyle: {
      backgroundColor: '#e6f0ea',
    },
  },
};

const UsersList = ({ userCenter, isAdmin }) => {
  const [users, setUsers] = useState([]);
  const [filterText, setFilterText] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [selectedAttendingFilter, setSelectedAttendingFilter] = useState('');
  const [selectedGenderFilter, setSelectedGenderFilter] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [locationOptions, setLocationOptions] = useState([]);
  const [togglingId, setTogglingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isExporting, setIsExporting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const listUsers = useMemo(() => httpsCallable(tbqFunctions, 'listUsers'), []);
  const listUsersMeta = useMemo(() => httpsCallable(tbqFunctions, 'listUsersMeta'), []);
  const updateUser = useMemo(() => httpsCallable(tbqFunctions, 'updateUser'), []);
  const mountedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filterText), 300);
    return () => clearTimeout(timer);
  }, [filterText]);

  const loadPage = useCallback(async (page, pageSize, searchText = debouncedSearch) => {
    if (!isAdmin && !userCenter) return;
    setLoading(true);
    try {
      const response = await listUsers({
        location: isAdmin ? selectedLocation : '',
        attendingMode: selectedAttendingFilter || '',
        gender: selectedGenderFilter || '',
        search: searchText || '',
        page,
        pageSize,
        sortField,
        sortDirection,
      });
      const data = response.data || {};
      setUsers(Array.isArray(data.rows) ? data.rows : []);
      setTotalRows(Number(data.totalRows || 0));
    } catch (error) {
      console.error('Failed to load users:', error);
      setUsers([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [
    isAdmin,
    userCenter,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    sortField,
    sortDirection,
    debouncedSearch,
    listUsers,
  ]);

  const toggleCoordinator = useCallback(async (user) => {
    setTogglingId(user.id);
    try {
      const newValue = !user.coordinator;
      await updateUser({
        userId: user.id,
        data: { coordinator: newValue },
      });
      setUsers(prev =>
        prev.map(u => u.id === user.id ? { ...u, coordinator: newValue } : u)
      );
    } catch (error) {
      console.error('Failed to update coordinator status:', error);
      alert('Failed to update coordinator status. Please try again.');
    } finally {
      setTogglingId(null);
    }
  }, [updateUser]);

  const handleSaveUser = useCallback(async (data) => {
    if (!editingUser?.id) return;

    setSavingUserEdit(true);
    try {
      await updateUser({
        userId: editingUser.id,
        data,
      });
      setEditingUser(null);
      await loadPage(currentPage, rowsPerPage, debouncedSearch);
    } catch (error) {
      console.error('Failed to update user:', error);
      throw new Error(error?.message || 'Failed to update user.');
    } finally {
      setSavingUserEdit(false);
    }
  }, [currentPage, debouncedSearch, editingUser, loadPage, rowsPerPage, updateUser]);

  useEffect(() => {
    if (!isAdmin) return;
    listUsersMeta({})
      .then((response) => {
        const locations = Array.isArray(response.data?.locations) ? response.data.locations : [];
        setLocationOptions(locations);
      })
      .catch((error) => {
        console.error('Failed to load user locations:', error);
        setLocationOptions([]);
      });
  }, [isAdmin, listUsersMeta]);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      loadPage(1, rowsPerPage, debouncedSearch);
      return;
    }
    setCurrentPage(1);
    loadPage(1, rowsPerPage, debouncedSearch);
  }, [
    userCenter,
    isAdmin,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    rowsPerPage,
    debouncedSearch,
    sortField,
    sortDirection,
    loadPage,
  ]);

  const columns = useMemo(() => {
    const cols = [
      { name: 'Name', selector: row => row.name, sortable: true, sortKey: 'name' },
      { name: 'Email', selector: row => row.email, sortable: true, sortKey: 'email' },
      { name: 'Mobile', selector: row => row.mobile, sortable: true, sortKey: 'mobile' },
      { name: 'Address', selector: row => row.address, sortable: true, wrap: true, sortKey: 'address' },
      { name: 'Age', selector: row => row.age, sortable: true, width: '70px', sortKey: 'age' },
      { name: 'Gender', selector: row => row.gender || 'N/A', sortable: true, width: '90px', center: true, sortKey: 'gender' },
      { name: 'Designation', selector: row => row.designation, sortable: true, wrap: true, sortKey: 'designation' },
      { name: 'Organization', selector: row => row.organization, sortable: true, wrap: true, sortKey: 'organization' },
      { name: 'Qualification', selector: row => row.qualification, sortable: true, wrap: true, sortKey: 'qualification' },
      { name: 'Residence Tel.', selector: row => row.telResidence, sortable: true, sortKey: 'telResidence' },
      { name: 'Location', selector: row => row.center || "N/A", sortable: true, wrap: true, minWidth: '180px', sortKey: 'center' },
      { name: 'Country', selector: row => row.country || "N/A", sortable: true, wrap: true, sortKey: 'country' },
      {
        name: 'Attending',
        selector: row => row.attendingAtLocation === false ? 0 : 1,
        cell: row => row.attendingAtLocation === false ? 'Online' : 'Yes',
        sortable: true,
        width: '100px',
        center: true,
        sortKey: 'attending',
      },
      {
        name: 'Tanzeem',
        selector: row => row.tanzeemMember ? 1 : 0,
        cell: row => row.tanzeemMember ? 'Yes' : 'No',
        sortable: true,
        width: '90px',
        center: true,
        sortKey: 'tanzeem',
      },
      {
        name: 'Created On',
        selector: row => row.createdOnMs ? new Date(row.createdOnMs).toLocaleString() : "N/A",
        sortable: true,
        width: '160px',
        sortKey: 'createdOn',
      },
    ];

    if (isAdmin) {
      cols.push({
        name: 'Coordinator',
        cell: (row) => (
          <button
            className={`coordinator-toggle ${row.coordinator ? 'is-coordinator' : ''}`}
            onClick={() => toggleCoordinator(row)}
            disabled={togglingId === row.id}
            title={row.coordinator ? 'Remove coordinator role' : 'Make coordinator'}
          >
            {togglingId === row.id ? '...' : row.coordinator ? 'Yes' : 'No'}
          </button>
        ),
        sortable: true,
        selector: row => row.coordinator ? 1 : 0,
        width: '110px',
        center: true,
        sortKey: 'coordinator',
      });

      cols.push({
        name: 'Actions',
        cell: (row) => (
          <div className="action-buttons">
            <button
              className="btn-edit"
              onClick={() => setEditingUser(row)}
              title="Edit user"
            >
              ✎
            </button>
          </div>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
        width: '80px',
        center: true,
      });
    }

    return cols;
  }, [isAdmin, toggleCoordinator, togglingId]);
  const filteredItems = users;

  const handlePageChange = async (page) => {
    setCurrentPage(page);
    await loadPage(page, rowsPerPage, debouncedSearch);
  };

  const handleRowsPerPageChange = async (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    await loadPage(1, newRowsPerPage, debouncedSearch);
  };

  const handleSort = (column, direction) => {
    const nextSortField = column.sortKey || 'id';
    setSortField(nextSortField);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const exportFilteredUsers = useCallback(async () => {
    setIsExporting(true);
    try {
      const exportRows = [];
      let page = 1;
      let hasMore = true;
      const exportPageSize = 100;

      while (hasMore) {
        const response = await listUsers({
          location: isAdmin ? selectedLocation : '',
          attendingMode: selectedAttendingFilter || '',
          gender: selectedGenderFilter || '',
          search: debouncedSearch || '',
          page,
          pageSize: exportPageSize,
          sortField,
          sortDirection,
        });
        const data = response.data || {};
        const rows = Array.isArray(data.rows) ? data.rows : [];
        exportRows.push(...rows);
        hasMore = Boolean(data.hasMore);
        page += 1;
      }
      const csv = convertToCSV(exportRows, columns);
      downloadCSV(csv, 'users-list.csv');
    } catch (error) {
      console.error('Failed to export users:', error);
      alert('Failed to export users. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, [
    listUsers,
    isAdmin,
    selectedLocation,
    selectedAttendingFilter,
    selectedGenderFilter,
    debouncedSearch,
    sortField,
    sortDirection,
    columns,
  ]);

  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="table-header">
        <FilterComponent
          onFilter={e => setFilterText(e.target.value)}
          filterText={filterText}
          isAdmin={isAdmin}
          locations={locationOptions}
          selectedLocation={selectedLocation}
          onLocationChange={(e) => setSelectedLocation(e.target.value)}
          onLocationClear={() => setSelectedLocation('')}
          selectedAttendingFilter={selectedAttendingFilter}
          onAttendingFilterChange={(e) => setSelectedAttendingFilter(e.target.value)}
          selectedGenderFilter={selectedGenderFilter}
          onGenderFilterChange={(e) => setSelectedGenderFilter(e.target.value)}
        />
        <div className="table-actions">
          <button className="btn-export" onClick={exportFilteredUsers} disabled={isExporting}>
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>
    );
  }, [filterText, isAdmin, locationOptions, selectedLocation, selectedAttendingFilter, selectedGenderFilter, isExporting, exportFilteredUsers]);

  return (
    <div className="dtq-locations-container">
      <DataTable
        title={(
          <div className="table-title">
            <span>Users</span>
            <span className="location-count">{filteredItems.length} of {totalRows} users</span>
          </div>
        )}
        columns={columns}
        data={filteredItems}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        persistTableHead
        progressPending={loading}
        progressComponent={<div className="loading-spinner">Loading users...</div>}
        pagination
        paginationServer
        paginationTotalRows={totalRows}
        paginationDefaultPage={currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        paginationPerPage={rowsPerPage}
        paginationRowsPerPageOptions={[10, 25, 50, 100]}
        sortServer
        onSort={handleSort}
        highlightOnHover
        responsive
        customStyles={customStyles}
        dense
      />
      {editingUser ? (
        <UserEditForm
          user={editingUser}
          locationOptions={locationOptions}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUser}
          saving={savingUserEdit}
        />
      ) : null}
    </div>
  );
};

export default UsersList;
